import React, { useState, useEffect } from "react";
import { FaUserEdit, FaSave } from "react-icons/fa";
import {
  MdEmail,
  MdPhone,
  MdLocationOn,
  MdOutlineCalendarToday,
} from "react-icons/md";
import { AiOutlineGlobal } from "react-icons/ai";
import axiosInstance from "../../utils/axios";
import uploadToCloudinary from "../../utils/cloudinary";
import toast from "react-hot-toast"; // Import toast

const defaultAvatars = {
  male: "https://cdn-icons-png.flaticon.com/512/236/236832.png",
  female: "https://cdn-icons-png.flaticon.com/512/236/236831.png",
  other: "https://cdn-icons-png.flaticon.com/512/236/236834.png",
};

const Profile = () => {
  const [profile, setProfile] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    gender: "other",
    dateOfBirth: "",
    address: "",
    nationality: "",
    profilePic: "",
  });

  const [isEditing, setIsEditing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    const storedData = JSON.parse(localStorage.getItem("mankia-adminData"));
    if (storedData) {
      setProfile((prev) => ({
        ...prev,
        ...storedData,
        profilePic: storedData.profilePic || "",
        dateOfBirth: storedData.dateOfBirth
          ? new Date(storedData.dateOfBirth).toISOString().split("T")[0]
          : "",
      }));
    }
  }, []);

  const handleChange = (e) => {
    setProfile({ ...profile, [e.target.name]: e.target.value });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);
    setProfile((prev) => ({ ...prev, profilePic: previewUrl }));
    setIsUploading(true);

    try {
      const uploadedUrl = await uploadToCloudinary(file);
      setProfile((prev) => ({ ...prev, profilePic: uploadedUrl }));
      toast.success("Profile picture uploaded successfully!");
    } catch (err) {
      console.error("Image upload failed:", err);
      toast.error("Failed to upload profile picture");
    } finally {
      setIsUploading(false);
    }
  };

  const handleSave = async () => {
    const savingToast = toast.loading("Saving profile...");

    try {
      const storedData = JSON.parse(localStorage.getItem("mankia-adminData"));
      if (!storedData?._id) {
        console.error("Admin ID not found in localStorage");
        toast.error("Admin ID not found");
        return;
      }

      const updatedProfile = {
        ...profile,
        dateOfBirth: profile.dateOfBirth
          ? new Date(profile.dateOfBirth).toISOString()
          : null,
      };

      const { data } = await axiosInstance.put(
        `/admins/admin-edit-profile/${storedData._id}`,
        updatedProfile
      );

      localStorage.setItem("mankia-adminData", JSON.stringify(data.admin));
      setProfile({
        ...data.admin,
        dateOfBirth: data.admin.dateOfBirth
          ? new Date(data.admin.dateOfBirth).toISOString().split("T")[0]
          : "",
      });
      setIsEditing(false);

      toast.success("Profile updated successfully!", { id: savingToast });
    } catch (error) {
      console.error(
        "Profile update error:",
        error.response?.data || error.message
      );
      toast.error(
        error.response?.data?.message || "Failed to update profile",
        { id: savingToast }
      );
    }
  };

  return (
    <div className="min-h-[85%] bg-gradient-to-tr from-slate-100 to-slate-200 flex items-center justify-center p-4">
      <div className="w-full max-w-3xl bg-white rounded-xl shadow-lg overflow-hidden">
        {/* Profile Header with Gradient Background */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-900 p-6 text-white">
          <div className="flex flex-col sm:flex-row items-center gap-6">
            <div className="relative w-24 h-24">
              <img
                src={profile.profilePic || defaultAvatars[profile.gender]}
                alt="Profile"
                className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
              />
              {isEditing && (
                <>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="absolute inset-0 opacity-0 cursor-pointer"
                  />
                  <div className="absolute bottom-0 right-0 bg-white rounded-full p-1 shadow-md">
                    <FaUserEdit className="text-blue-500 text-sm" />
                  </div>
                </>
              )}
              {isUploading && (
                <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
            </div>
            <div className="text-center sm:text-left">
              <h2 className="text-2xl font-bold">
                {profile.name || "Your Name"}
              </h2>
              <p className="text-blue-100">
                {profile.userType?.toUpperCase() || "ADMIN"}
              </p>
            </div>
          </div>
        </div>

        {/* Profile Form */}
        <div className="p-6 space-y-4">
          {/* Name and Email */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <span className="text-blue-500">
                <FaUserEdit />
              </span>
              <input
                type="text"
                name="name"
                value={profile.name || ""}
                onChange={handleChange}
                disabled={!isEditing}
                placeholder="Full Name"
                className="flex-1 px-3 py-2 border-b-2 border-gray-200 focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div className="flex items-center gap-3">
              <span className="text-blue-500">
                <MdEmail />
              </span>
              <input
                type="email"
                name="email"
                value={profile.email || ""}
                onChange={handleChange}
                disabled={!isEditing}
                placeholder="Email"
                className="flex-1 px-3 py-2 border-b-2 border-gray-200 focus:border-blue-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Phone and Nationality */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <span className="text-blue-500">
                <MdPhone />
              </span>
              <input
                type="tel"
                name="phoneNumber"
                value={profile.phoneNumber || ""}
                onChange={handleChange}
                disabled={!isEditing}
                placeholder="Phone Number"
                className="flex-1 px-3 py-2 border-b-2 border-gray-200 focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div className="flex items-center gap-3">
              <span className="text-blue-500">
                <AiOutlineGlobal />
              </span>
              <input
                type="text"
                name="nationality"
                value={profile.nationality || ""}
                onChange={handleChange}
                disabled={!isEditing}
                placeholder="Nationality"
                className="flex-1 px-3 py-2 border-b-2 border-gray-200 focus:border-blue-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Compact Row for Gender, DOB */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <span className="text-blue-500">
                <FaUserEdit />
              </span>
              <select
                name="gender"
                value={profile.gender}
                onChange={handleChange}
                disabled={!isEditing}
                className="flex-1 px-3 py-2 border-b-2 border-gray-200 focus:border-blue-500 focus:outline-none bg-white"
              >
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-blue-500">
                <MdOutlineCalendarToday />
              </span>
              <input
                type="date"
                name="dateOfBirth"
                value={profile.dateOfBirth || ""}
                onChange={handleChange}
                disabled={!isEditing}
                className="flex-1 px-3 py-2 border-b-2 border-gray-200 focus:border-blue-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Address (full width) */}
          <div className="flex items-start gap-3">
            <span className="text-blue-500 mt-2">
              <MdLocationOn />
            </span>
            <textarea
              name="address"
              value={profile.address || ""}
              onChange={handleChange}
              disabled={!isEditing}
              placeholder="Address"
              rows="2"
              className="flex-1 px-3 py-2 border-b-2 border-gray-200 focus:border-blue-500 focus:outline-none resize-none"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="px-6 pb-6 flex justify-end">
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="px-5 py-2 bg-primaryColor text-white rounded-md hover:bg-blue-600 transition-colors flex items-center gap-2 shadow-md hover:shadow-lg"
            >
              <FaUserEdit /> Edit Profile
            </button>
          ) : (
            <button
              onClick={handleSave}
              className="px-5 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors flex items-center gap-2 shadow-md hover:shadow-lg"
            >
              <FaSave /> Save Changes
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;